import {
  ArrowRightIcon,
  Ban,
  Calendar,
  FileText,
  Folder,
  Link2,
  Loader2,
  User,
} from 'lucide-react';
import React, { useEffect, useState } from 'react';

import {
  ExternalTaskLink,
  MatterLink,
  TaskLink,
} from '@/components/entity-links';
import { cn } from '@/lib/utils';
import {
  ExternalTaskPreviewSchema,
  ExternalTaskRuleType,
  InternalTaskPreviewSchema,
  LiveTestResultSchema,
  PreviewExternalFilterResponseSchema,
  PreviewInternalFilterResponseSchema,
} from '@/services/api/firmsService';
import { formatTaskDate } from '@/utils/taskDateUtils';

const PREVIEW_LIMIT_OPTIONS = [
  { value: 100, label: '100' },
  { value: 500, label: '500' },
  { value: 1_000, label: '1,000' },
  { value: 5_000, label: '5,000' },
  { value: 10_000, label: '10,000' },
  { value: 50_000, label: '50,000' },
];

/**
 * Hook to debounce the loading state to prevent flashing.
 * Only shows loading after a short delay, avoiding flash for quick responses.
 */
const useDebouncedLoading = (
  isLoading: boolean,
  hasData: boolean,
  delay = 200,
): boolean => {
  const [debouncedLoading, setDebouncedLoading] = useState(false);

  useEffect(() => {
    if (isLoading && !hasData) {
      const timer = setTimeout(() => setDebouncedLoading(true), delay);
      return () => clearTimeout(timer);
    }
    setDebouncedLoading(false);
    return () => {};
  }, [isLoading, hasData, delay]);

  return debouncedLoading;
};

type PreviewMode = 'preview' | 'external' | 'internal';

interface ModeSelectorProps {
  mode: PreviewMode;
  onModeChange: (mode: PreviewMode) => void;
  ruleType: ExternalTaskRuleType;
}

const ModeSelector: React.FC<ModeSelectorProps> = ({
  mode,
  onModeChange,
  ruleType,
}) => {
  const isInternalDisabled = ruleType === 'EXCLUSION';

  return (
    <div className="flex items-center gap-1 rounded-xl bg-slate-100 p-1">
      <button
        type="button"
        onClick={() => onModeChange('preview')}
        className={cn(
          'rounded-lg px-4 py-2 text-sm font-medium transition-all duration-150',
          mode === 'preview'
            ? 'bg-white text-slate-900 shadow-sm'
            : 'text-slate-500 hover:text-slate-700',
        )}
      >
        Preview
      </button>
      <button
        type="button"
        onClick={() => onModeChange('external')}
        className={cn(
          'rounded-lg px-4 py-2 text-sm font-medium transition-all duration-150',
          mode === 'external'
            ? 'bg-white text-slate-900 shadow-sm'
            : 'text-slate-500 hover:text-slate-700',
        )}
      >
        External
      </button>
      <button
        type="button"
        onClick={() => !isInternalDisabled && onModeChange('internal')}
        disabled={isInternalDisabled}
        className={cn(
          'rounded-lg px-4 py-2 text-sm font-medium transition-all duration-150',
          isInternalDisabled
            ? 'cursor-not-allowed text-slate-300'
            : mode === 'internal'
              ? 'bg-white text-slate-900 shadow-sm'
              : 'text-slate-500 hover:text-slate-700',
        )}
        title={isInternalDisabled ? 'Not applicable for exclusion rules' : ''}
      >
        Internal
      </button>
    </div>
  );
};

interface LimitSelectorProps {
  limit: number;
  onLimitChange: (limit: number) => void;
}

const LimitSelector: React.FC<LimitSelectorProps> = ({
  limit,
  onLimitChange,
}) => {
  return (
    <div className="flex items-center gap-2">
      <span className="text-xs font-medium text-slate-500">Limit:</span>
      <select
        value={limit}
        onChange={(e) => onLimitChange(Number(e.target.value))}
        className="h-8 rounded-lg border border-slate-200 bg-white px-2 text-xs font-medium text-slate-700 focus:border-slate-300 focus:outline-none focus:ring-2 focus:ring-slate-100"
      >
        {PREVIEW_LIMIT_OPTIONS.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
    </div>
  );
};

interface LivePreviewPanelProps {
  mode: PreviewMode;
  onModeChange: (mode: PreviewMode) => void;
  combinedData: LiveTestResultSchema | undefined;
  externalData: PreviewExternalFilterResponseSchema | undefined;
  internalData: PreviewInternalFilterResponseSchema | undefined;
  isLoadingCombined: boolean;
  isLoadingExternal: boolean;
  isLoadingInternal: boolean;
  hasExternalFilters: boolean;
  hasInternalFilters: boolean;
  ruleType: ExternalTaskRuleType;
  limit: number;
  onLimitChange: (limit: number) => void;
}

const getLoadingState = (
  mode: PreviewMode,
  isLoadingCombined: boolean,
  isLoadingExternal: boolean,
  isLoadingInternal: boolean,
): boolean => {
  if (mode === 'preview') return isLoadingCombined;
  if (mode === 'external') return isLoadingExternal;
  return isLoadingInternal;
};

const getHasData = (
  mode: PreviewMode,
  combinedData: LiveTestResultSchema | undefined,
  externalData: PreviewExternalFilterResponseSchema | undefined,
  internalData: PreviewInternalFilterResponseSchema | undefined,
): boolean => {
  if (mode === 'preview') return Boolean(combinedData);
  if (mode === 'external') return Boolean(externalData);
  return Boolean(internalData);
};

export const LivePreviewPanel: React.FC<LivePreviewPanelProps> = ({
  mode,
  onModeChange,
  combinedData,
  externalData,
  internalData,
  isLoadingCombined,
  isLoadingExternal,
  isLoadingInternal,
  hasExternalFilters,
  hasInternalFilters,
  ruleType,
  limit,
  onLimitChange,
}) => {
  const hasBothFilters = hasExternalFilters && hasInternalFilters;

  const isLoading = getLoadingState(
    mode,
    isLoadingCombined,
    isLoadingExternal,
    isLoadingInternal,
  );

  const hasData = getHasData(mode, combinedData, externalData, internalData);

  const showLoadingSkeleton = useDebouncedLoading(isLoading, hasData, 200);

  if (showLoadingSkeleton) {
    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <LimitSelector limit={limit} onLimitChange={onLimitChange} />
          <ModeSelector
            mode={mode}
            onModeChange={onModeChange}
            ruleType={ruleType}
          />
        </div>
        <LoadingState />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <LimitSelector limit={limit} onLimitChange={onLimitChange} />
          {isLoading && (
            <Loader2 className="h-4 w-4 animate-spin text-slate-400" />
          )}
        </div>
        <ModeSelector
          mode={mode}
          onModeChange={onModeChange}
          ruleType={ruleType}
        />
      </div>

      {mode === 'preview' &&
        (ruleType === 'EXCLUSION' ? (
          <ExclusionPreviewView
            data={combinedData}
            hasExternalFilters={hasExternalFilters}
          />
        ) : (
          <PreviewView data={combinedData} hasBothFilters={hasBothFilters} />
        ))}

      {mode === 'external' && <ExternalView data={externalData} />}

      {mode === 'internal' && <InternalView data={internalData} />}
    </div>
  );
};

// Preview view - combined matches with arrows
const PreviewView: React.FC<{
  data: LiveTestResultSchema | undefined;
  hasBothFilters: boolean;
}> = ({ data, hasBothFilters }) => {
  if (!hasBothFilters) {
    return (
      <div className="flex flex-col items-center justify-center rounded-xl border border-dashed border-slate-200 bg-slate-50/50 py-12 text-center">
        <div className="rounded-2xl bg-slate-100 p-4">
          <Link2 className="h-8 w-8 text-slate-400" />
        </div>
        <p className="mt-4 text-sm font-medium text-slate-600">
          Configure both filters
        </p>
        <p className="mt-1 text-xs text-slate-500">
          Set external and internal filters to see link preview
        </p>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-center">
        <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
        <p className="mt-4 text-sm text-slate-500">Loading preview...</p>
      </div>
    );
  }

  const externalMatches = (data.matches ?? []).filter(
    (m) => m.trigger_task_type === 'EXTERNAL',
  );

  return (
    <div className="space-y-4">
      {externalMatches.length === 0 ? (
        <div className="flex flex-col items-center justify-center rounded-xl border border-dashed border-slate-200 bg-slate-50/50 py-12 text-center">
          <FileText className="h-8 w-8 text-slate-300" />
          <p className="mt-4 text-sm font-medium text-slate-600">
            No matches found
          </p>
          <p className="mt-1 text-xs text-slate-500">
            Try adjusting your filter criteria
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {externalMatches.map((match, index) => (
            <div
              key={`${match.trigger_task_id}-${index}`}
              className="overflow-hidden rounded-xl border border-slate-200 bg-white"
            >
              {/* Matter header */}
              <div className="flex items-center gap-2 border-b border-slate-100 bg-slate-50 px-4 py-2.5">
                <Folder className="h-3.5 w-3.5 text-slate-400" />
                <MatterLink
                  entityId={match.matter_id}
                  openInNewTab
                  className="text-xs font-medium text-slate-600 hover:text-slate-900"
                >
                  {match.matter_name || 'Unknown Matter'}
                </MatterLink>
              </div>

              <div className="flex items-stretch">
                <div className="flex-1 bg-amber-50/30 p-4">
                  <p className="text-[10px] font-semibold uppercase tracking-wider text-amber-600">
                    External Task
                  </p>
                  <ExternalTaskLink
                    entityId={match.trigger_task_id}
                    openInNewTab
                    className="mt-1 block text-sm font-medium leading-tight text-slate-900 hover:text-slate-700"
                  >
                    {match.trigger_task_name}
                  </ExternalTaskLink>
                </div>

                <div className="flex items-center justify-center bg-gradient-to-r from-amber-50/30 to-blue-50/30 px-4">
                  <div className="flex h-8 w-8 items-center justify-center rounded-full bg-white shadow-sm">
                    <ArrowRightIcon className="h-4 w-4 text-slate-400" />
                  </div>
                </div>

                <div className="flex-1 bg-blue-50/30 p-4">
                  <p className="text-[10px] font-semibold uppercase tracking-wider text-blue-600">
                    Internal Task{match.matched_tasks.length !== 1 ? 's' : ''}
                  </p>
                  {match.matched_tasks.length === 0 ? (
                    <p className="mt-1 text-sm italic text-slate-400">
                      No matches
                    </p>
                  ) : (
                    <div className="mt-1 space-y-1.5">
                      {match.matched_tasks.map((task, taskIndex) => (
                        <div
                          key={`${task.task_id}-${taskIndex}`}
                          className="flex items-center justify-between gap-2"
                        >
                          <TaskLink
                            entityId={task.task_id}
                            matterId={match.matter_id}
                            openInNewTab
                            className="line-clamp-1 text-sm leading-tight text-slate-700 hover:text-slate-900"
                          >
                            {task.task_name}
                          </TaskLink>
                          {task.would_create_link && (
                            <span className="shrink-0 rounded-full bg-emerald-100 px-2 py-0.5 text-[10px] font-semibold text-emerald-700">
                              New
                            </span>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Summary Stats */}
      <div className="grid grid-cols-3 gap-3">
        <StatCard
          label="Evaluated"
          value={data.total_trigger_tasks_evaluated}
          color="blue"
        />
        <StatCard label="Matches" value={data.total_matches} color="emerald" />
        <StatCard
          label="New Links"
          value={data.total_links_would_create ?? 0}
          color="purple"
        />
      </div>

      <p className="text-center text-xs text-slate-400">
        Preview shows up to 10 matches. Actual rule will apply to all matching
        tasks.
      </p>
    </div>
  );
};

// Exclusion preview view - shows tasks that would be excluded
const ExclusionPreviewView: React.FC<{
  data: LiveTestResultSchema | undefined;
  hasExternalFilters: boolean;
}> = ({ data, hasExternalFilters }) => {
  if (!hasExternalFilters) {
    return (
      <div className="flex flex-col items-center justify-center rounded-xl border border-dashed border-slate-200 bg-slate-50/50 py-12 text-center">
        <div className="rounded-2xl bg-slate-100 p-4">
          <Ban className="h-8 w-8 text-slate-400" />
        </div>
        <p className="mt-4 text-sm font-medium text-slate-600">
          Configure external filters
        </p>
        <p className="mt-1 text-xs text-slate-500">
          Set filters to see exclusion preview
        </p>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-center">
        <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
        <p className="mt-4 text-sm text-slate-500">Loading preview...</p>
      </div>
    );
  }

  const excludedTasks = data.excluded_tasks || [];

  return (
    <div className="space-y-4">
      {excludedTasks.length === 0 ? (
        <div className="flex flex-col items-center justify-center rounded-xl border border-dashed border-slate-200 bg-slate-50/50 py-12 text-center">
          <FileText className="h-8 w-8 text-slate-300" />
          <p className="mt-4 text-sm font-medium text-slate-600">
            No tasks would be excluded
          </p>
          <p className="mt-1 text-xs text-slate-500">
            Try adjusting your filter criteria
          </p>
        </div>
      ) : (
        <div className="space-y-2">
          {excludedTasks.map((task) => (
            <div
              key={task.task_id}
              className="rounded-xl border border-amber-200 bg-amber-50/50 p-4"
            >
              <div className="flex items-start justify-between gap-2">
                <div className="flex items-center gap-2.5">
                  <div className="flex h-6 w-6 items-center justify-center rounded-full bg-amber-100">
                    <Ban className="h-3.5 w-3.5 text-amber-600" />
                  </div>
                  <span className="text-sm font-medium leading-tight text-slate-900">
                    {task.task_name}
                  </span>
                </div>
                <span
                  className={cn(
                    'shrink-0 rounded-full px-2.5 py-0.5 text-xs font-semibold',
                    task.already_excluded
                      ? 'bg-slate-100 text-slate-500'
                      : 'bg-amber-100 text-amber-700',
                  )}
                >
                  {task.already_excluded ? 'Already Excluded' : 'Will Exclude'}
                </span>
              </div>

              {task.matter_name && (
                <div className="mt-2 flex items-center gap-1.5 pl-8 text-xs text-slate-500">
                  <Folder className="h-3 w-3" />
                  {task.matter_name}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Summary Stats */}
      <div className="grid grid-cols-2 gap-3">
        <StatCard
          label="Evaluated"
          value={data.total_trigger_tasks_evaluated}
          color="blue"
        />
        <StatCard
          label="Would Exclude"
          value={data.total_tasks_would_exclude || 0}
          color="amber"
        />
      </div>

      <p className="text-center text-xs text-slate-400">
        Preview shows up to 10 matches. Actual rule will apply to all matching
        tasks.
      </p>
    </div>
  );
};

// External tasks view
const ExternalView: React.FC<{
  data: PreviewExternalFilterResponseSchema | undefined;
}> = ({ data }) => {
  if (!data) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-center">
        <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
        <p className="mt-4 text-sm text-slate-500">Loading...</p>
      </div>
    );
  }

  if (data.tasks.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center rounded-xl border border-dashed border-slate-200 bg-slate-50/50 py-12 text-center">
        <FileText className="h-8 w-8 text-slate-300" />
        <p className="mt-4 text-sm font-medium text-slate-600">
          No matching external tasks
        </p>
        <p className="mt-1 text-xs text-slate-500">
          Adjust your filters to see matching tasks
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <span className="text-sm font-medium text-slate-700">
          {data.total_count.toLocaleString()} external task
          {data.total_count !== 1 ? 's' : ''} match
        </span>
        {data.tasks.length < data.total_count && (
          <span className="text-xs text-slate-400">
            Showing {data.tasks.length.toLocaleString()} of{' '}
            {data.total_count.toLocaleString()}
          </span>
        )}
      </div>

      <div className="space-y-2">
        {data.tasks.map((task: ExternalTaskPreviewSchema) => (
          <div
            key={task.id}
            className="rounded-xl border border-slate-200 bg-white p-4 transition-colors hover:border-slate-300"
          >
            <div className="flex items-start justify-between gap-3">
              <div className="line-clamp-2 min-w-0 flex-1 text-sm font-medium leading-tight">
                <ExternalTaskLink
                  entityId={task.id}
                  openInNewTab
                  className="text-slate-900 hover:text-slate-700"
                >
                  {task.name}
                </ExternalTaskLink>
              </div>
              {task.status && (
                <span className="shrink-0 rounded-full bg-slate-100 px-2.5 py-0.5 text-xs font-medium capitalize text-slate-600">
                  {task.status.toLowerCase().replace('_', ' ')}
                </span>
              )}
            </div>

            <div className="mt-3 flex flex-wrap items-center gap-4 text-xs text-slate-500">
              {task.matter_id && (
                <MatterLink
                  entityId={task.matter_id}
                  openInNewTab
                  className="flex items-center gap-1.5 hover:text-slate-700"
                >
                  <Folder className="h-3.5 w-3.5" />
                  {task.matter_name || 'View Matter'}
                </MatterLink>
              )}
              {task.assignee_name && (
                <span className="flex items-center gap-1.5">
                  <User className="h-3.5 w-3.5" />
                  {task.assignee_name}
                </span>
              )}
              {task.due_date && (
                <span className="flex items-center gap-1.5">
                  <Calendar className="h-3.5 w-3.5" />
                  {formatTaskDate(task.due_date)}
                </span>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// Internal tasks view
const InternalView: React.FC<{
  data: PreviewInternalFilterResponseSchema | undefined;
}> = ({ data }) => {
  if (!data) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-center">
        <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
        <p className="mt-4 text-sm text-slate-500">Loading...</p>
      </div>
    );
  }

  if (data.tasks.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center rounded-xl border border-dashed border-slate-200 bg-slate-50/50 py-12 text-center">
        <FileText className="h-8 w-8 text-slate-300" />
        <p className="mt-4 text-sm font-medium text-slate-600">
          No matching internal tasks
        </p>
        <p className="mt-1 text-xs text-slate-500">
          Adjust your filters to see matching tasks
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <span className="text-sm font-medium text-slate-700">
          {data.total_count.toLocaleString()} internal task
          {data.total_count !== 1 ? 's' : ''} match
        </span>
        {data.tasks.length < data.total_count && (
          <span className="text-xs text-slate-400">
            Showing {data.tasks.length.toLocaleString()} of{' '}
            {data.total_count.toLocaleString()}
          </span>
        )}
      </div>

      <div className="space-y-2">
        {data.tasks.map((task: InternalTaskPreviewSchema) => (
          <div
            key={task.id}
            className="rounded-xl border border-slate-200 bg-white p-4 transition-colors hover:border-slate-300"
          >
            <div className="line-clamp-2 text-sm font-medium leading-tight">
              <TaskLink
                entityId={task.id}
                matterId={task.matter_id}
                openInNewTab
                className="text-slate-900 hover:text-slate-700"
              >
                {task.name}
              </TaskLink>
            </div>

            <div className="mt-3 flex flex-wrap items-center gap-4 text-xs text-slate-500">
              {task.matter_id && (
                <MatterLink
                  entityId={task.matter_id}
                  openInNewTab
                  className="flex items-center gap-1.5 hover:text-slate-700"
                >
                  <Folder className="h-3.5 w-3.5" />
                  {task.matter_name || 'View Matter'}
                </MatterLink>
              )}
              {task.task_type_name && (
                <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-600">
                  {task.task_type_name}
                </span>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

const StatCard: React.FC<{
  label: string;
  value: number;
  color: 'blue' | 'emerald' | 'purple' | 'amber';
}> = ({ label, value, color }) => {
  const colorClasses = {
    blue: 'text-blue-600',
    emerald: 'text-emerald-600',
    purple: 'text-purple-600',
    amber: 'text-amber-600',
  };

  return (
    <div className="rounded-xl border border-slate-200 bg-slate-50 p-4 text-center">
      <p
        className={cn('text-2xl font-bold tracking-tight', colorClasses[color])}
      >
        {value.toLocaleString()}
      </p>
      <p className="mt-1 text-[10px] font-semibold uppercase tracking-wider text-slate-500">
        {label}
      </p>
    </div>
  );
};

const LoadingState = () => (
  <div className="space-y-3">
    {[1, 2, 3].map((i) => (
      <div key={i} className="h-24 animate-pulse rounded-xl bg-slate-100" />
    ))}
  </div>
);
